import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { axiosPost } from '../util/api'
import { clearAndFetchUser } from '../actions/user'
import { Toasts } from '../components/toaster/Toast'
import { type EntityId, idKey } from '../types/constants'
import Profile = Models.Profile

/**
 * MER-2468: shared frontend hook for the "Re-check connection" action.
 *
 * Three surfaces invoke this (data-connections table V1, V2, and the
 * profile-scoped TopCallouts banner). Centralising the network call,
 * status handling, and toast wording in one place means a fix in any
 * branch lands for all three. Previously each surface had a near-but-
 * not-identical copy; this collapses them into one code path.
 */

export type RecheckStatus = 'connected' | 'disconnected' | 'verification_failed' | 'error'

export interface RecheckResult {
    /**
     * - connected:           Amazon returned 200, unlinked_at cleared
     * - disconnected:        Amazon returned 401, unlinked_at unchanged / re-set
     * - verification_failed: Amazon was flaky (5xx, 429, network blip).
     *                        State NOT changed. Backed by HTTP 502, since
     *                        MerchJar is acting as a gateway to Amazon for
     *                        this request and Amazon gave a bad response.
     * - error:               unexpected client / network / MerchJar-side error
     */
    status: RecheckStatus
    /** Latest profile snapshot from the server, when available. */
    profile?: Profile
    /** Whatever the server (or transport layer) said. */
    message?: string
}

export const useRecheckProfileLink = () => {
    const dispatch = useDispatch()

    // Track the in-flight profile id (not a boolean) so the table's
    // "block other rows while one is in flight" logic still works,
    // and so a single hook instance can drive the banner *and* a
    // table without them stepping on each other.
    const [recheckingProfileId, setRecheckingProfileId] = useState<EntityId | null>(null)

    const recheck = async (profile: Profile): Promise<RecheckResult> => {
        setRecheckingProfileId(profile.profile_id)
        try {
            // `ignore: true` suppresses util/api.tsx's global error toast.
            // Without it, every non-2xx response double-toasts: once from
            // `handle()` (with the raw backend message, e.g. Laravel's
            // bare "Server Error") and once from this hook's catch block
            // below (with the friendly per-status copy). Letting the hook
            // own all toasting keeps the 422/502/5xx wording consistent.
            const res = await axiosPost(
                `profiles/${profile.profile_id}/recheck-link`,
                null,
                false,
                { ignore: true },
            )
            const updated = res?.data?.profile as Profile | undefined
            const message = res?.data?.message as string | undefined

            // Refresh the user so any consumer of activeProfile (e.g. the
            // TopCallouts banner) sees unlinked_at flip to null and unmounts.
            dispatch(clearAndFetchUser())
            Toasts.success(message ?? 'Connection restored.')
            return { status: 'connected', profile: updated, message }
        } catch (err: any) {
            const status = err?.response?.status as number | undefined
            const backendMessage = err?.response?.data?.message as string | undefined
            const updated = err?.response?.data?.profile as Profile | undefined

            // No response object at all = transport-level failure (DNS, offline,
            // CORS, request cancelled, etc.). Distinguish from server-side errors
            // because the user's recovery action is different (check internet vs.
            // wait for us to fix it).
            if (!err?.response) {
                // eslint-disable-next-line no-console
                console.error('[useRecheckProfileLink] network error', err)
                Toasts.error(
                    "Couldn't reach MerchJar. Check your internet connection and try again.",
                )
                return { status: 'error', message: 'network' }
            }

            // 422: Amazon still says no. Backend message is intentional and
            // already actionable ("re-authorize in Amazon Ads") — pass it through.
            if (status === 422) {
                Toasts.warning(
                    backendMessage ??
                        'Still unauthorized. Re-authorize MerchJar in Amazon Ads, then try again.',
                )
                return { status: 'disconnected', profile: updated, message: backendMessage }
            }

            // 502: transient upstream-side error (Amazon gave us a 5xx, 429,
            // network blip, etc). Distinct from a 5xx from MerchJar itself:
            // it's not our bug, just a "try again in a minute" situation.
            // We pick 502 over 503 because we're acting as a gateway to
            // Amazon for this request — 503 would imply MerchJar itself
            // is overloaded, which it isn't.
            if (status === 502) {
                Toasts.warning(
                    backendMessage ??
                        "Amazon didn't respond clearly. Please try again in a minute.",
                )
                return {
                    status: 'verification_failed',
                    profile: updated,
                    message: backendMessage,
                }
            }

            // 419: Laravel session/CSRF expired. The user's session timed out
            // while the page was open. Refreshing the page mints a new token.
            if (status === 419) {
                Toasts.error('Your session expired. Refresh the page and try again.')
                return { status: 'error', message: 'session_expired' }
            }

            // 403: profile doesn't belong to this user. Should only happen if
            // the user fiddles with URLs or holds a stale profile reference.
            if (status === 403) {
                Toasts.error("You don't have permission to re-check this connection.")
                return { status: 'error', message: 'forbidden' }
            }

            // 404: profile was deleted between the page load and this click.
            if (status === 404) {
                Toasts.error(
                    'This profile no longer exists. Refresh the page to see your current profiles.',
                )
                return { status: 'error', message: 'not_found' }
            }

            // 5xx (other than 502 above): MerchJar's bug, not Amazon's. The
            // backend catches its own throws and returns a friendly message;
            // if it didn't (raw Laravel "Server Error"), prefer our copy.
            if (typeof status === 'number' && status >= 500) {
                // eslint-disable-next-line no-console
                console.error('[useRecheckProfileLink] server error', { status, err })
                const isGenericLaravel = !backendMessage || /^server error$/i.test(backendMessage)
                Toasts.error(
                    isGenericLaravel
                        ? "MerchJar couldn't process this request. We've been notified — please try again in a few minutes."
                        : (backendMessage as string),
                )
                return { status: 'error', message: backendMessage ?? 'server_error' }
            }

            // Anything else (other 4xx). Show backend message if it's specific,
            // otherwise a generic but still user-friendly fallback. Avoid
            // surfacing raw HTTP codes to end users.
            // eslint-disable-next-line no-console
            console.error('[useRecheckProfileLink] unexpected error', { status, err })
            Toasts.error(
                backendMessage ??
                    "We couldn't re-check this connection. Please try again, or contact support if it keeps happening.",
            )
            return { status: 'error', message: backendMessage }
        } finally {
            setRecheckingProfileId(null)
        }
    }

    // MER-2802: profile_id may be a number or a string. Compare on String(...).
    const isRechecking = (profileId: EntityId) =>
        recheckingProfileId !== null && idKey(recheckingProfileId) === idKey(profileId)
    const isAnyRechecking = recheckingProfileId !== null

    return {
        recheck,
        recheckingProfileId,
        isRechecking,
        isAnyRechecking,
    }
}
